import React from 'react'
import { totalAreaAcres, totalAreaSqFt } from '../utils/polygonArea'

const formatNumber = (n) => {
  if (n == null || isNaN(n)) return '—'
  return n >= 1000 ? `${Math.round(n / 1000)}K` : String(n)
}

const ServiceArea = ({ data }) => {
  const headline = data?.headline || 'Service Area Details'
  const mapImageUrl = data?.mapImageUrl || ''
  const mapCaption = data?.mapCaption || ''
  const propertyStats = data?.propertyStats || []
  const propertyLines = data?.propertyLines || []
  const serviceAreas = (data?.serviceAreas || []).filter(area => Array.isArray(area.paths) && area.paths.length >= 3)
  const hasServiceAreas = serviceAreas.length > 0

  // Acreage computed from the saved polygons themselves
  const acres = hasServiceAreas ? totalAreaAcres(serviceAreas) : 0
  const sqFt = hasServiceAreas ? totalAreaSqFt(serviceAreas) : 0

  // Derive stats from property data if propertyStats not manually set
  const stats = hasServiceAreas
    ? [
        { value: serviceAreas.length, label: serviceAreas.length === 1 ? 'Service Area' : 'Service Areas' },
        ...(acres >= 0.05 ? [{ value: acres.toFixed(acres >= 10 ? 0 : 1), label: 'Total Acres' }] : []),
        ...(sqFt >= 100 ? [{ value: formatNumber(Math.round(sqFt)), label: 'Sq Ft Maintained' }] : []),
      ]
    : propertyStats.length > 0 ? propertyStats : (() => {
    let totalAcres = 0
    let totalSqFt = 0
    propertyLines.forEach(pl => {
      if (pl.acres) totalAcres += parseFloat(pl.acres) || 0
      if (pl.sf) totalSqFt += parseFloat(pl.sf) || 0
    })
    const s = []
    if (totalAcres > 0) s.push({ value: totalAcres.toFixed(1), label: 'Acres' })
    if (totalSqFt > 0) s.push({ value: formatNumber(totalSqFt), label: 'Sq Ft' })
    return s
  })()

  // Address and APN from first parcel or data
  const address = data?.address || ''
  const apn = propertyLines[0]?.APN || data?.apn || ''

  // "Service Areas:" line — user override wins; otherwise summarize by
  // acreage instead of listing unnamed polygons ("Service Area, Service Area").
  const customNames = serviceAreas
    .map(area => area.name)
    .filter(name => name && name !== 'Service Area')
  const acreageText = acres >= 0.05 ? `${acres.toFixed(acres >= 10 ? 0 : 2)} acres maintained` : ''
  const autoLine = customNames.length > 0
    ? [customNames.join(', '), acreageText].filter(Boolean).join(' · ')
    : [`${serviceAreas.length} ${serviceAreas.length === 1 ? 'area' : 'areas'}`, acreageText].filter(Boolean).join(' · ')
  const serviceAreaNames = hasServiceAreas ? (data?.serviceAreasLabel || autoLine) : ''

  return (
    <div className="proposal-section">
      <div className="proposal-section__headline">{headline}</div>
      <div className="proposal-section__accent-line" />

      {mapImageUrl && (
        <div className="proposal-service-area__map">
          <img src={mapImageUrl} alt={hasServiceAreas ? 'Service areas' : 'Property service area'} />
        </div>
      )}

      {stats.length > 0 && (
        <div className="proposal-service-area__stats">
          {stats.map((stat, i) => (
            <div className="proposal-service-area__stat" key={i}>
              <div className="proposal-service-area__stat-value">{stat.value}</div>
              <div className="proposal-service-area__stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {mapCaption && (
        <div className="proposal-service-area__caption">{mapCaption}</div>
      )}

      {(serviceAreaNames || address || apn) && (
        <div className="proposal-service-area__details">
          {serviceAreaNames && <div className="proposal-service-area__detail"><strong>Service Areas:</strong> {serviceAreaNames}</div>}
          {address && <div className="proposal-service-area__detail"><strong>Property Address:</strong> {address}</div>}
          {apn && <div className="proposal-service-area__detail"><strong>APN:</strong> {apn}</div>}
        </div>
      )}
    </div>
  )
}

export default ServiceArea
